import { useState } from "react"
import { Button, Input } from "@aix/ui"
import { ReactTable } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"
import TrackableFormModal from "./components/TrackableFormModal"

export default function TrackablesListTab() {
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<string>("all")
  const [windowFilter, setWindowFilter] = useState<string>("all")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [editingTrackable, setEditingTrackable] = useState<any>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Queries
  const { data: trackables, isLoading, refetch } = trpc.trackables.getAllTrackables.useQuery()
  const utils = trpc.useUtils()

  // Mutations
  const archiveMutation = trpc.trackables.archiveTrackable.useMutation({
    onSuccess: () => {
      toast.success("Trackable archived (data preserved)")
      void refetch()
    },
    onError: (error) => {
      toast.error(`Failed to archive: ${error.message}`)
    },
  })

  const restoreMutation = trpc.trackables.restoreTrackable.useMutation({
    onSuccess: () => {
      toast.success("Trackable restored")
      void refetch()
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`)
    },
  })

  // Filter trackables
  const filteredTrackables = trackables?.filter((t) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      if (!t.name.toLowerCase().includes(query) && !t.promptText.toLowerCase().includes(query)) {
        return false
      }
    }

    // Category filter
    if (categoryFilter !== "all" && t.category !== categoryFilter) {
      return false
    }

    // Window filter
    if (windowFilter !== "all" && t.checkInWindow !== windowFilter) {
      return false
    }

    // Status filter
    if (statusFilter === "active" && !t.isActive) return false
    if (statusFilter === "inactive" && t.isActive) return false

    return true
  })

  const handleArchive = (trackableId: string, name: string) => {
    if (confirm(`Archive "${name}"? It will be hidden but all historical data will be preserved.`)) {
      archiveMutation.mutate({ trackableId })
    }
  }

  const handleRestore = (trackableId: string) => {
    restoreMutation.mutate({ trackableId })
  }

  const handleEdit = (trackable: any) => {
    setEditingTrackable(trackable)
    setIsFormModalOpen(true)
  }

  const handleCreate = () => {
    setEditingTrackable(null)
    setIsFormModalOpen(true)
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "body":
        return "border-l-4 border-teal-500 bg-teal-50"
      case "work":
        return "border-l-4 border-yellow-500 bg-yellow-50"
      case "reflection":
        return "border-l-4 border-purple-500 bg-purple-50"
      case "relationship":
        return "border-l-4 border-pink-500 bg-pink-50"
      case "business":
        return "border-l-4 border-cyan-500 bg-cyan-50"
      default:
        return ""
    }
  }

  const getCategoryEmoji = (category: string) => {
    switch (category) {
      case "body":
        return "💪"
      case "work":
        return "💼"
      case "reflection":
        return "🧠"
      case "relationship":
        return "❤️"
      case "business":
        return "📈"
      default:
        return "📌"
    }
  }

  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row }: any) => (
        <div className={`p-2 ${getCategoryColor(row.original.category)} ${!row.original.isActive ? "opacity-50" : ""}`}>
          <span className="font-semibold">
            {!row.original.isActive && "📦 "}
            {row.original.name}
          </span>
        </div>
      ),
    },
    {
      Header: "Prompt",
      accessor: "promptText",
      Cell: ({ row }: any) => (
        <div className="text-sm text-muted-foreground max-w-md truncate">{row.original.promptText}</div>
      ),
    },
    {
      Header: "Type",
      accessor: "dataType",
      Cell: ({ row }: any) => (
        <span className="text-xs px-2 py-1 bg-secondary border border-border rounded">{row.original.dataType}</span>
      ),
    },
    {
      Header: "Unit",
      accessor: "unit",
      Cell: ({ row }: any) => <span className="text-sm">{row.original.unit || "-"}</span>,
    },
    {
      Header: "Category",
      accessor: "category",
      Cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <span>{getCategoryEmoji(row.original.category)}</span>
          <span className="capitalize">{row.original.category}</span>
        </div>
      ),
    },
    {
      Header: "Window",
      accessor: "checkInWindow",
      Cell: ({ row }: any) => (
        <span className="text-xs px-2 py-1 bg-secondary border border-border rounded capitalize">
          {row.original.checkInWindow}
        </span>
      ),
    },
    {
      Header: "Sort",
      accessor: "sortOrder",
      Cell: ({ row }: any) => <span className="text-sm">{row.original.sortOrder}</span>,
    },
    {
      Header: "Status",
      accessor: "isActive",
      Cell: ({ row }: any) => (
        <span
          className={`text-xs px-2 py-1 rounded ${
            row.original.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
          }`}
        >
          {row.original.isActive ? "✅ Active" : "📦 Archived"}
        </span>
      ),
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ row }: any) => (
        <div className="flex gap-2">
          <Button
            onClick={() => handleEdit(row.original)}
            size="sm"
            variant="outline"
            className="border-2 border-border"
          >
            ✏️ Edit
          </Button>
          {row.original.isActive ? (
            <Button
              onClick={() => handleArchive(row.original.id, row.original.name)}
              size="sm"
              variant="outline"
              className="border-2 border-border text-orange-700"
              loading={archiveMutation.isLoading}
            >
              📦 Archive
            </Button>
          ) : (
            <Button
              onClick={() => handleRestore(row.original.id)}
              size="sm"
              variant="outline"
              className="border-2 border-border text-green-700"
              loading={restoreMutation.isLoading}
            >
              ♻️ Restore
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-primary">Trackables Management</h2>
        <Button onClick={handleCreate} className="border-2 border-border bg-primary text-primary-foreground">
          ➕ Create New
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-secondary border-4 border-border p-4 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold mb-2">Search</label>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name or prompt..."
              className="border-2 border-border"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-semibold mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full border-2 border-border p-2 bg-background rounded"
            >
              <option value="all">All Categories</option>
              <option value="body">💪 Body</option>
              <option value="work">💼 Work</option>
              <option value="reflection">🧠 Reflection</option>
              <option value="relationship">❤️ Relationship</option>
              <option value="business">📈 Business</option>
            </select>
          </div>

          {/* Window Filter */}
          <div>
            <label className="block text-sm font-semibold mb-2">Window</label>
            <select
              value={windowFilter}
              onChange={(e) => setWindowFilter(e.target.value)}
              className="w-full border-2 border-border p-2 bg-background rounded"
            >
              <option value="all">All Windows</option>
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border-2 border-border p-2 bg-background rounded"
            >
              <option value="all">All Status</option>
              <option value="active">✅ Active Only</option>
              <option value="inactive">📦 Archived Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-secondary border-4 border-border p-4 shadow-lg">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading trackables...</div>
        ) : filteredTrackables && filteredTrackables.length > 0 ? (
          <ReactTable data={filteredTrackables} columns={columns} enableSorting={true} enablePagination={true} />
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No trackables found</p>
            <Button onClick={handleCreate} className="border-2 border-border">
              Create Your First Trackable
            </Button>
          </div>
        )}
      </div>

      {/* Form Modal */}
      <TrackableFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false)
          setEditingTrackable(null)
        }}
        trackable={editingTrackable}
        onSuccess={() => {
          void refetch()
          setIsFormModalOpen(false)
          setEditingTrackable(null)
        }}
      />
    </div>
  )
}
